import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ConfigService } from './shared/services/core/config.service';
import { BehaviorSubject, from, Subject, catchError, of } from 'rxjs';
import packageJson from '../../package.json';
import { KeycloakService } from 'keycloak-angular';
import { UserDataService } from './shared/services/user-data.service';
import { MenuItem } from './shared/interfaces/Menu/menuItem.model';
import {
  AppSelectorComponent,
  AppSkeletonComponent,
} from '@hygie31/ngx-hygie31-kit-ui-17';
import { ProgressSpinnerComponent } from './shared/components/progress-spinner/progress-spinner.component';
import { UserSessionService } from './shared/services/user.service';
import { Router } from '@angular/router';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  imports: [AppSkeletonComponent, ProgressSpinnerComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  readonly KEY_CURRENT_USER = 'currentUserAdmin';
  currentUser: BehaviorSubject<string>;
  urlredirectionUi: string[] = [];
  title = 'ADMIN - Hygie31';
  appName = 'Admin';
  version = packageJson.version;
  onlySideMenuIcon = false;
  displayedApp: string[] = []; // Filter the apps that should be displayed in the app-selector
  urlRedirectionApp: { [key: string]: string } = {};
  menu = [] as any[];
  activiteAdherent: string = '';
  menuConfig: Record<string, MenuItem[]> = {
    fournisseur: [
      {
        icon: 'science',
        name: 'Fournisseurs',
        route: 'fournisseurs',
      },
    ],
    adherent: [
      {
        icon: 'local_hospital',
        name: 'Adhérents',
        route: 'adherents',
      },
    ],
    cockpitAd: [
      {
        icon: 'people_alt',
        name: 'PFA Interne',
        route: 'pfa-interne',
      },
    ],
    gestioncomptes: [
      {
        icon: 'people_alt',
        name: 'Gestion des comptes',
        route: 'gestioncomptes',
      },
    ],
  };
  firstname = '';
  lastname = '';
  isIframe = false;

  constructor(
    private readonly configService: ConfigService,
    private readonly titleService: Title,
    private readonly keycloakService: KeycloakService,
    private readonly userDataService: UserDataService,
    private readonly userSessionService: UserSessionService,
    private readonly router: Router,
  ) {
    this.currentUser = new BehaviorSubject('');
    this.titleService.setTitle(this.title);
  }

  ngOnInit() {
    // Define redirection URLs for different apps
    this.urlRedirectionApp = {
      DECLAF: this.configService.config.modules.declaf,
      GESTAC: this.configService.config.modules.gestac,
      COCKPIT: this.configService.config.modules.cockpit,
      ADMIN: this.configService.config.modules.admin,
    };
    // Check if the app is running in an iframe
    this.isIframe = window !== window.parent && !window.opener;

    from(this.keycloakService.loadUserProfile())
      .pipe(
        catchError(() => {
          // Fallback: build profile from token when /account endpoint is blocked by CORS
          const token = this.keycloakService.getKeycloakInstance().tokenParsed;
          return of({
            email: token?.['email'] ?? '',
            firstName: token?.['given_name'] ?? '',
            lastName: token?.['family_name'] ?? '',
            username: token?.['preferred_username'] ?? '',
          });
        }),
      )
      .subscribe((userDetails) => {
        const groups = this.keycloakService.getUserRoles();
        this.setMenuByUserProfile(userDetails, groups);

        let user = {
          ...userDetails,
          groups,
        };

        this.setCurrentUser(JSON.stringify(user));
        this.setActiviteAdherent(groups);
        this.feedDisplayedApp(groups);
      });
  }
  /**
   * Sets the current user in the app and stores it in the session storage.
   * @param currentUser The current user to set.
   */
  private setCurrentUser(currentUser: string) {
    this.currentUser.next(currentUser);
    localStorage[this.KEY_CURRENT_USER] = currentUser;
    this.userDataService.updateUserData(currentUser);
  }
  /**
   * Sets the menu items based on the user's profile and user role flags.
   *
   * Dynamically constructs a menu by considering the user's profile and role flags. The menu options
   * will vary depending on the user's role, such as Catalogue Manager, Hygie31, Supplier, or ADV.
   *
   * @param {Object} user - The user object representing the user's profile.
   * @param groups
   * @param {boolean} user.isCatalogueManager - Indicates if the user is a Catalogue Manager.
   * @param {boolean} user.isHygie31 - Indicates if the user is a Catalogue Manager Hygie31.
   * @param {boolean} user.isFournisseur - Indicates if the user is a Supplier.z
   * @param {boolean} user.isADV - Indicates if the user is an ADV.
   */
  private setMenuByUserProfile(user: any, groups: string[]): void {
    const userMenus: MenuItem[] = [];

    this.firstname = user.firstName;
    this.lastname = user.lastName;

    const menuMap: Record<string, string[]> = {
      'app-admin.administrer': [
        'fournisseur',
        'adherent',
        'cockpitAd',
        'gestioncomptes',
      ],
      'app-admin-gpo.administrer': ['adherent', 'gestioncomptes'],
    };

    // Parcourir chaque groupe et ajouter les noms de menu correspondants
    for (const key in menuMap) {
      if (groups?.includes(key)) {
        const menuNames = menuMap[key];
        menuNames.forEach((menuName) => {
          const menuItems = this.menuConfig[menuName];
          if (menuItems) {
            userMenus.push(...menuItems);
          }
        });
      }
    }

    // ajout des "traductions" pour les groupes
    this.addAdminMenuItems(groups, menuMap);

    // Update the menu property with the generated user-specific menu.
    this.menu = userMenus;
  }

  /**
   * Adds admin menu items based on the user's groups.
   * @param groups - The user's groups.
   * @param menuMapTitulaireAdmin - The map of menu items for the admin.
   */
  private addAdminMenuItems(
    groups: string[],
    menuMapTitulaireAdmin: Record<string, string[]>,
  ): void {
    if (groups.includes('app-admin.administrer')) {
      // localStorage['adminCockpit'] = [];
      let adminItems: { role: string; name: string }[] = [];
      for (const key in menuMapTitulaireAdmin) {
        if (groups?.includes(key)) {
          const menuNames = menuMapTitulaireAdmin[key];
          menuNames.forEach((menuName) => {
            const menuItems = this.menuConfig[menuName];
            if (menuItems) {
              menuItems.forEach((menuItem) => {
                adminItems.push({ role: key, name: menuItem.name });
              });
            }
          });
        }
      }
      localStorage['adminCockpit'] = JSON.stringify(adminItems);
    }
  }

  /**
   * Sets the displayed app based on the given roles.
   * @param {string[]} roles - An array of roles.
   * @return {void}
   */
  private feedDisplayedApp(roles: string[]): void {
    let distinctRoles = roles
      .map((role) => role.split('.')[0])
      .filter((role, index, self) => self.indexOf(role) === index);

    this.displayedApp = [];

    const roleAppMap: any = {
      gestac: 'Gestac',
      pfa: 'Cockpit',
      'pfa-gpo': 'Cockpit',
      'app-admin': 'Admin',
      'app-admin-gpo': 'Admin',
      declaf: 'Declaf',
    };

    for (const role of distinctRoles) {
      if (roleAppMap[role]) {
        this.displayedApp.push(roleAppMap[role]);
      }
    }
  }

  /**
   * Détermine l'activité adhérent selon les groupes Keycloak.
   * - Si uniquement "pfa." présent : pharma
   * - Si uniquement "pfa-opt." présent : optique
   * - Si les deux présents : dsi
   */
  private setActiviteAdherent(groups: string[]): void {
    const hasPharma = groups.some((g) => g.startsWith('app-admin.'));
    const hasOptique = groups.some((g) => g.startsWith('app-admin-opt.'));
    const hasGpo = groups.some((g) => g.startsWith('app-admin-gpo.'));

    if (hasPharma && !hasOptique) {
      this.activiteAdherent = 'pharma';
    } else if (hasPharma && hasOptique) {
      this.activiteAdherent = 'dsi';
    } else if (hasGpo) {
      this.activiteAdherent = 'gpo';
      this.router.navigate(['adherents']);
    } else if (hasOptique) {
      this.activiteAdherent = 'optique';
    } else {
      this.activiteAdherent = '';
    }
    this.userSessionService.setActivite(this.activiteAdherent);
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Clears sessionStorage and localStorage and logs out the current user.
   */
  onLogout() {
    this.keycloakService.logout();
  }
}
